<template>
  <layout-report :renderBackAndForthFooter="false" :isProgressDone="true" :showFooter="false">
    <v-container class="padded">
      <v-row align="center">
        <v-col cols="12" sm="8">
          <v-card flat>
            <v-card-title class="text-h4 px-0">{{ $t("report.thgBillingOverview.title") }} 💸</v-card-title>
            <v-card-subtitle class="pl-0">{{ $t("report.thgBillingOverview.description") }}</v-card-subtitle>
          </v-card>
        </v-col>
        <v-col cols="12" sm="4">
          <v-spacer />
          <div class="text-center" v-if="isEnabled">
            <v-menu open-on-hover bottom offset-y left>
              <template v-slot:activator="{ on, attrs }">
                <v-list-item two-line v-bind="attrs" v-on="on">
                  <v-list-item-content>
                    <v-skeleton-loader type="list-item" v-if="isLoading" />
                    <v-list-item-title v-else>
                      <span class="text-h6 mr-1 font-weight-light"> € </span>
                      <span class="text-h4 font-weight-bold"> {{ euroCentSplit(incomingTotal)[0] }} </span>
                      <span class="text-h6 ml-n2 mr-n1 font-weight-light">,</span>
                      <span class="text-h6 font-weight-light"> {{ euroCentSplit(incomingTotal)[1] }} </span>
                    </v-list-item-title>
                    <v-list-item-subtitle> {{ $t("report.thgBillingOverview.incomingTotal") }} </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </template>

              <v-list>
                <v-list-item two-line>
                  <v-list-item-avatar>
                    <v-icon>mdi-credit-card-check-outline</v-icon>
                  </v-list-item-avatar>
                  <v-list-item-content class="text-right">
                    <v-list-item-title class="text-h6 font-weight-light">
                      {{ incomingCustomerAccount }}€
                    </v-list-item-title>
                    <v-list-item-subtitle>
                      {{ $t("report.thgBillingOverview.incomingCustomerAccount") }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                <v-divider />
                <v-list-item>
                  <v-list-item-avatar>
                    <v-icon>mdi-tree-outline</v-icon>
                  </v-list-item-avatar>
                  <v-list-item-content class="text-right">
                    <v-list-item-title class="text-h6 font-weight-light"> {{ outgoingTrees }}€ </v-list-item-title>
                    <v-list-item-subtitle> {{ $t("report.thgBillingOverview.outgoingTrees") }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                <v-divider />
                <v-list-item>
                  <v-list-item-avatar>
                    <v-icon>mdi-ev-station</v-icon>
                  </v-list-item-avatar>
                  <v-list-item-content class="text-right">
                    <v-list-item-title class="text-h6 font-weight-light"> {{ outgoingCharging }}€ </v-list-item-title>
                    <v-list-item-subtitle> {{ $t("report.thgBillingOverview.outgoingCharging") }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                <v-divider />
                <v-list-item>
                  <v-list-item-avatar>
                    <v-icon>mdi-handshake-outline</v-icon>
                  </v-list-item-avatar>
                  <v-list-item-content class="text-right">
                    <v-list-item-title class="text-h6 font-weight-light"> {{ incomingAffiliate }}€ </v-list-item-title>
                    <v-list-item-subtitle>
                      {{ $t("report.thgBillingOverview.incomingAffiliate") }}</v-list-item-subtitle
                    >
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-menu>
          </div>
        </v-col>
      </v-row>
      <v-row v-if="!isLoading">
        <v-col cols="12" class="px-1">
          <thg-billing-overview-card />
        </v-col>
      </v-row>
      <v-row v-else>
        <v-col cols="12">
          <v-card class="mx-2">
            <v-skeleton-loader type="table-heading, table-tbody, table-tfoot"></v-skeleton-loader>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </layout-report>
</template>

<script lang="ts">
import ThgBillingOverviewCard from "@/components/thg/ThgBillingOverviewCard.vue";
import LayoutReport from "@/layouts/LayoutReport.vue";
import { Component, Vue } from "vue-property-decorator";
import { BillingModule } from "@/store/modules/billing.store";
import { ThgModule } from "@/store/modules/thg.store";
import { AccountModule } from "@/store/modules/account.store";
import { handleError } from "@/lib/utility/handleError";
import { BillingTypeEnum } from "@/lib/enum/billingType.enum";
import { FeatureModule } from "@/store/modules/feature.store";
import { formatCommaEuroToDottedComma } from "@/lib/utility/number-helper";

@Component({
  components: {
    ThgBillingOverviewCard,
    LayoutReport
  }
})
export default class ThgBillingOverview extends Vue {
  CHARGING_ACCOUNT = 16160;
  TREES_ACCOUNT = 16150;

  isLoading = false;
  accountNumber: number | null = null;

  incomingTotal = "0,00";
  incomingCustomerAccount = "0,00";
  incomingAffiliate = "0,00";
  outgoingTrees = "0,00";
  outgoingCharging = "0,00";

  get isEnabled() {
    return FeatureModule.isBillingDistributionEnabled;
  }

  get myBillings() {
    return BillingModule.myBillings;
  }
  async mounted() {
    this.isLoading = true;

    await Promise.all([this.loadBillings(), this.loadThgs()]);
    if (this.myBillings.length) {
      await this.getUserAccountNumber();
    }
    this.calculatePayoutDistribution();

    this.isLoading = false;
  }

  euroCentSplit(s: string) {
    return s.split(",");
  }

  async getUserAccountNumber() {
    try {
      this.accountNumber = await AccountModule.getUserAccountNumber();
    } catch (e) {
      handleError(e);
    }
  }

  calculatePayoutDistribution() {
    let incomingTotal = 0;
    let incomingCustomerAccount = 0;
    let incomingAffiliate = 0;
    let outgoingTrees = 0;
    let outgoingCharging = 0;

    this.myBillings.forEach((b) => [
      b.accountRecord.forEach((ar: any) => {
        if (ar?.account === this.accountNumber) {
          incomingTotal += ar.amount;
          if (b.type === BillingTypeEnum.CREDIT_CUSTOMER_ACCOUNT) {
            incomingCustomerAccount += ar?.amount;
          }
          if (b.type === BillingTypeEnum.AFFILIATE) {
            incomingAffiliate += ar?.amount;
          }
        }
        if (ar?.contraAccount === this.accountNumber) {
          if (ar?.account === this.TREES_ACCOUNT) {
            outgoingTrees += ar?.amount;
          }
          if (ar?.account === this.CHARGING_ACCOUNT) {
            outgoingCharging += ar?.amount;
          }
        }
      })
    ]);

    this.incomingTotal = formatCommaEuroToDottedComma(incomingTotal);
    this.incomingCustomerAccount = formatCommaEuroToDottedComma(incomingCustomerAccount);
    this.incomingAffiliate = formatCommaEuroToDottedComma(incomingAffiliate);
    this.outgoingTrees = formatCommaEuroToDottedComma(outgoingTrees);
    this.outgoingCharging = formatCommaEuroToDottedComma(outgoingCharging);
  }

  async loadThgs() {
    try {
      if (!ThgModule.myThgs?.length) {
        await ThgModule.getMyThgs();
      }
    } catch (e) {
      handleError(e);
    }
  }

  async loadBillings() {
    try {
      await BillingModule.setMyBillings();
    } catch (e) {
      handleError(e);
    }
  }
}
</script>
